import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, CircleDashed, Link2, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { api } from '../lib/api';
import { usePilotContext } from '../hooks/usePilotContext';
import { toast } from 'sonner';
import MagicBento from '../components/MagicBento';
import { VERIFICATION_BENTO_CARDS, VERIFICATION_BENTO_GLOW } from '../data/magicBentoPresets';

export default function VerificationLoopPage() {
  const { context } = usePilotContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!context.customer_id) return;
    setLoading(true);
    try {
      const res = await api.get(`/api/pilot/verification-loop/${context.customer_id}`);
      setData(res);
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Failed to load verification loop');
    } finally {
      setLoading(false);
    }
  }, [context.customer_id]);

  useEffect(() => {
    load();
  }, [load]);

  const steps = data?.steps || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Verified fix loop</h1>
          <p className="text-muted-foreground">
            Discovery → risk → attack paths → remediation → resimulation → auditor attestation. This page
            shows whether each link in the chain has produced evidence for your tenant.
          </p>
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-primary">
            <Link className="hover:underline" to="/dashboard">
              Overview
            </Link>
            <Link className="hover:underline" to="/risk">
              Risk
            </Link>
            <Link className="hover:underline" to="/remediation">
              Remediation
            </Link>
            <Link className="hover:underline" to="/resimulation">
              Resimulation
            </Link>
            <Link className="hover:underline" to="/compliance">
              Compliance
            </Link>
          </div>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={() => load()} disabled={loading}>
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <section className="space-y-3" aria-labelledby="verification-bento-heading">
        <h2
          id="verification-bento-heading"
          className="font-heading text-lg font-semibold tracking-tight text-foreground"
        >
          Proof chain highlights
        </h2>
        <MagicBento
          cards={VERIFICATION_BENTO_CARDS}
          glowColor={VERIFICATION_BENTO_GLOW}
          enableSpotlight={false}
          particleCount={8}
        />
      </section>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5 text-primary" />
              Pipeline status
            </CardTitle>
            <CardDescription>
              {data?.narrative || 'Aligns OT engineer, lab operator, GRC, and platform admin on one proof chain.'}
            </CardDescription>
          </div>
          {data && (
            <span
              className={
                data.loop_complete
                  ? 'rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 py-1 text-xs font-medium text-emerald-600'
                  : 'rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1 text-xs font-medium text-amber-700'
              }
            >
              {data.loop_complete ? 'Loop complete' : 'In progress'}
            </span>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {loading && !data ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (
            <ul className="space-y-2">
              {steps.map((s) => {
                const ok = s.status === 'ok';
                return (
                  <li
                    key={s.id}
                    className="flex items-start gap-3 rounded-md border border-border bg-secondary/20 p-3 text-sm"
                  >
                    {ok ? (
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                    ) : (
                      <CircleDashed className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                    )}
                    <div>
                      <div className="font-medium capitalize text-foreground">{s.id.replace(/_/g, ' ')}</div>
                      <div className="text-xs text-muted-foreground">
                        <span className="font-mono">{s.status}</span> — {s.detail}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
